import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Skeleton,
  Typography,
} from "@mui/material";
import { toLocaleDateTimeString } from "@utils";
import { Scorecard } from "@components";
import VideoBox from "./VideoBox";
import {
  OrderOut,
  useWebApiV1ListAttachmentsQuery,
  useWebApiV1ListWalkthroughAttachmentsQuery,
} from "@providers/hop-ord-server/api";
import { skipToken } from "@reduxjs/toolkit/query";
import ImageCarousell from "./ImageCarousel";

type CustomCardContentProps = {
  isLoading: boolean;
  isEmpty: boolean;
  contentDisplayType: "scorecard" | "plan walkthrough" | "image";
  children: React.ReactNode;
};

const CustomCardContent = ({
  children,
  isEmpty,
  contentDisplayType,
  isLoading,
}: CustomCardContentProps) => {
  return (
    <CardContent>
      {isLoading && <Skeleton variant="rectangular" height={100} />}
      {!isLoading && isEmpty && (
        <Typography variant="body1">
          No {contentDisplayType} submitted.
        </Typography>
      )}
      {!isLoading && !isEmpty && children}
    </CardContent>
  );
};

const PhysicianReviewSkeleton = () => {
  return (
    <>
      <Skeleton variant="rectangular" height={32} width={120} />
      <Skeleton variant="rectangular" height={80} />
      <Skeleton variant="rectangular" height={268} />
      <Skeleton variant="rectangular" height={268} />
      <Skeleton variant="rectangular" height={268} />
    </>
  );
};

type Props = {
  order?: OrderOut;
};

const PhysicianReviewContent = ({ order }: Props) => {
  const orderId = order?.id;

  const {
    data: scorecardAttachmentsData,
    isLoading: scorecardAttachmentsLoading,
  } = useWebApiV1ListAttachmentsQuery(
    orderId !== undefined
      ? {
          orderId,
        }
      : skipToken,
  );

  const scorecardsAvailable =
    !!scorecardAttachmentsData?.scorecards &&
    scorecardAttachmentsData.scorecards.length > 0;

  const {
    data: walkthroughAttachmentsData,
    isLoading: walkthroughAttachmentsLoading,
  } = useWebApiV1ListWalkthroughAttachmentsQuery(
    orderId !== undefined
      ? {
          orderId,
        }
      : skipToken,
  );

  const walkthroughs = walkthroughAttachmentsData?.filter(
    (w) => w.fileName.search(/\.(mp4|mov|webm)$/i) !== -1,
  );

  const walkthroughsAvailable = walkthroughs && walkthroughs.length > 0;

  const images = walkthroughAttachmentsData?.filter(
    (w) => w.fileName.search(/\.(jpg|jpeg|png|webp)$/i) !== -1,
  );

  const imagesAvailable = images && images.length > 0;

  // Use the sentForReviewDatetime from the order object
  const readyForApprovalDatetime = order?.sentForReviewDatetime;

  if (!order) {
    return <PhysicianReviewSkeleton />;
  }

  return (
    <>
      <Typography
        variant="body1"
        color="text.primary"
        data-testid="submitted-datetime"
      >
        Submitted:{" "}
        {readyForApprovalDatetime
          ? toLocaleDateTimeString(readyForApprovalDatetime)
          : "-"}
      </Typography>
      {order.planReviewSummary && (
        <Box>
          <Typography fontWeight={500} display="inline">
            Summary:{" "}
          </Typography>
          <Typography variant="body1" display="inline">
            {order.planReviewSummary}
          </Typography>
        </Box>
      )}
      <Card variant="outlined" data-testid="scorecard-card">
        <CardHeader
          title={<Typography variant="h6">Scorecard</Typography>}
          subheader="Dosimetric criteria displaying plan actuals against goals"
        />
        <Divider />
        <CustomCardContent
          isLoading={scorecardAttachmentsLoading}
          isEmpty={!scorecardsAvailable}
          contentDisplayType="scorecard"
        >
          {scorecardsAvailable && (
            <Scorecard
              data={scorecardAttachmentsData?.scorecards[0].scorecardObj}
            />
          )}
        </CustomCardContent>
      </Card>
      <Card variant="outlined" data-testid="plan-walkthrough-card">
        <CardHeader
          title={<Typography variant="h6">Plan Walkthrough</Typography>}
          subheader="Screen recordings displaying isodose distributions"
        />
        <Divider />
        <CustomCardContent
          isLoading={walkthroughAttachmentsLoading}
          isEmpty={!walkthroughsAvailable}
          contentDisplayType="plan walkthrough"
        >
          {walkthroughsAvailable && (
            <VideoBox
              src={walkthroughs[0].file}
              data-testid="walkthrough-video"
            />
          )}
        </CustomCardContent>
      </Card>
      <Card variant="outlined" data-testid="images-card">
        <CardHeader title={<Typography variant="h6">Images</Typography>} />
        <Divider />
        <CustomCardContent
          isLoading={walkthroughAttachmentsLoading}
          isEmpty={!imagesAvailable}
          contentDisplayType="image"
        >
          {imagesAvailable && <ImageCarousell images={images} />}
        </CustomCardContent>
      </Card>
    </>
  );
};

export default PhysicianReviewContent;
